import { css } from "@emotion/react";
import styled from "@emotion/styled/macro";
import { tokens, Typography } from "@sunrun/experience-ui-components";
import React, { RefObject, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";

const CAP_NAV_HEIGHT_EXPANDED = 56;
const CAP_NAV_HEIGHT_COMPACT = 44;
const CAP_NAV_COMPACT_SCROLL_THRESHOLD = 80;

const getCapStickyNavOffset = (isCompact: boolean) =>
  isCompact ? CAP_NAV_HEIGHT_COMPACT : CAP_NAV_HEIGHT_EXPANDED;

const useCapNavCompact = (
  threshold = CAP_NAV_COMPACT_SCROLL_THRESHOLD
): boolean => {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsCompact(window.scrollY > threshold);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [threshold]);

  return isCompact;
};

/** True when the sticky nav sentinel has scrolled out of view (nav is pinned). */
const useCapStickyNavStuck = (
  sentinelRef: RefObject<HTMLElement | null>,
  enabled: boolean
): boolean => {
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsStuck(false);
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [enabled, sentinelRef]);

  return isStuck;
};

const CapStickyNavSentinel = styled.div`
  width: 100%;
  height: 0;
  pointer-events: none;
`;

type CapSectionTitleProps = {
  children: React.ReactNode;
};

const CapSectionTitle = ({ children }: CapSectionTitleProps) => {
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  return (
    <Typography
      color={tokens.BRAND_HERO_BLUE}
      lineHeight={isTablet ? 34 : 48}
      size={isTablet ? 28 : 40}
      style={{
        marginTop: isMobile ? 16 : 24,
        marginBottom: isMobile ? 16 : 24,
      }}
    >
      {children}
    </Typography>
  );
};

const StyledCapStepSection = styled.section<{ $scrollMarginTop: number }>`
  scroll-margin-top: ${({ $scrollMarginTop }) => $scrollMarginTop}px;
  width: 100%;

  &:not(:first-of-type) {
    padding-top: 32px;
    border-top: 1px solid ${tokens.BORDER_LIGHT};
    margin-top: 16px;
  }

  &:not(:last-of-type) {
    margin-bottom: 64px;
  }
`;

type CapStepSectionProps = React.ComponentPropsWithoutRef<"section"> & {
  isCompact?: boolean;
};

const CapStepSection = ({
  isCompact = false,
  ...props
}: CapStepSectionProps) => (
  <StyledCapStepSection
    $scrollMarginTop={getCapStickyNavOffset(isCompact)}
    {...props}
  />
);

const capStickyNavInnerStyles = css`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
  }

  @media (min-width: 1700px) {
    min-width: 1220px;
  }
`;

const CapLegacyStickyNavShell = styled.div`
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  background: ${tokens.BACKGROUND_LIGHT};
  padding: 0;
`;

const CapLegacyStickyNavInner = styled.div<{
  isMobile: boolean;
  isTablet: boolean;
}>`
  ${capStickyNavInnerStyles}
  padding: ${({ isMobile, isTablet }) =>
    isMobile ? "0 16px" : isTablet ? "0 24px" : "0 64px"};
`;

const CapSimplifiedStickyNavShell = styled.div<{ $isStuck?: boolean }>`
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  background: ${({ $isStuck }) =>
    $isStuck
      ? "rgba(174, 206, 254, 1)" /* #AECFFE; closest token TINTS_CHARGE_BLUE_60 */
      : "transparent"};
  padding: 0 64px;
`;

const CapSimplifiedStickyNavInner = styled.div<{
  isMobile: boolean;
  isTablet: boolean;
}>`
  ${capStickyNavInnerStyles}
  padding: 16px 0;
`;

export {
  CAP_NAV_COMPACT_SCROLL_THRESHOLD,
  CAP_NAV_HEIGHT_COMPACT,
  CAP_NAV_HEIGHT_EXPANDED,
  CapLegacyStickyNavInner,
  CapLegacyStickyNavShell,
  CapSectionTitle,
  CapSimplifiedStickyNavInner,
  CapSimplifiedStickyNavShell,
  CapStepSection,
  CapStickyNavSentinel,
  getCapStickyNavOffset,
  useCapNavCompact,
  useCapStickyNavStuck,
};
